import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'

export const Card = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode
    topComponent?: React.ReactNode
    onClick?: React.MouseEventHandler
    padding?: 'sm' | 'none'
    className?: string
    testId?: string
  }
>(({ children, topComponent, onClick, testId = 'ContentCard', padding = 'sm', className }, ref) => {
  return (
    <CardStyled
      ref={ref}
      data-testid={testId}
      data-has-click={!!onClick}
      data-padding={padding}
      className={className}
    >
      <div>{topComponent}</div>

      <div onClick={onClick}>{children}</div>
    </CardStyled>
  )
})

Card.displayName = 'Card'

// ======
// Styles
// ======

const CardStyled = styled.div({
  '--borderRadius': radius.lgx3,

  overflow: 'clip',
  '@supports not (overflow: "clip")': { overflow: 'hidden' },

  border: '1px solid ' + colors.neutral[300],
  borderRadius: 'var(--borderRadius)',
  boxShadow: shadow.xs,
  fontFamily: font.family.primary,

  '> div:first-of-type': {
    overflow: 'clip',
    '@supports not (overflow: "clip")': { overflow: 'hidden' },
    borderTopLeftRadius: 'var(--borderRadius)',
    borderTopRightRadius: 'var(--borderRadius)',

    ':empty': { display: 'none' },
  },

  '> div:last-of-type': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    padding: 0,
    backgroundColor: colors.white,
    borderBottomLeftRadius: 'var(--borderRadius)',
    borderBottomRightRadius: 'var(--borderRadius)',
  },

  '&[data-has-click="true"]': {
    userSelect: 'none',
    transition: 'all 0.3s ease',

    '@media (hover: hover) and (pointer: fine)': {
      '> div:last-of-type': { transition: 'all 0.3s ease' },
      '&:hover > div:last-of-type': { backgroundColor: colors.surface[50] },
    },

    '&:active:not(:has(button:active))': { transform: 'scale(0.95)' },
    '&:active:not(:has(button:active)) > *': { opacity: 0.75 },
  },

  '&[data-padding="sm"]': {
    '> div:last-of-type': {
      padding: spacing.sm,
    },
  },
})
